import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import fxService from '../services/fxService';

// Create the context
const CurrencyContext = createContext(null);

// Account currency is always AUD (system base currency)
const ACCOUNT_CURRENCY = 'AUD';

/**
 * CurrencyProvider component
 * Provides global reporting currency state and update functions to all child components.
 * This ensures consistent currency display across the entire application.
 */
export function CurrencyProvider({ children }) {
  // Watch auth state so we re-fetch currency when user logs in/out
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  // Reporting currency preference (default AUD until loaded from backend)
  const [reportingCurrency, setReportingCurrency] = useState(ACCOUNT_CURRENCY);

  // Loading state - true until initial currency preference is fetched
  const [isLoading, setIsLoading] = useState(true);

  // Version counter - incremented when currency changes to trigger re-fetches across the app
  // Components can include this in their useEffect dependencies to refetch data when currency changes
  const [currencyVersion, setCurrencyVersion] = useState(0);

  // Error state for initial load failures
  const [loadError, setLoadError] = useState(null);

  // Load user's reporting currency preference on mount and after login
  useEffect(() => {
    if (!isAuthenticated) {
      // Reset to default when logged out
      setReportingCurrency(ACCOUNT_CURRENCY);
      setIsLoading(false);
      return;
    }

    const loadReportingCurrency = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);

        const result = await fxService.getReportingCurrency();

        if (result.success && result.data?.reportingCurrency) {
          setReportingCurrency(result.data.reportingCurrency);
        } else {
          // Default to AUD if no preference set
          setReportingCurrency(ACCOUNT_CURRENCY);
        }
      } catch (err) {
        console.error('Failed to load reporting currency preference:', err);
        setLoadError(err.message || 'Failed to load currency preference');
        // Default to AUD on error
        setReportingCurrency(ACCOUNT_CURRENCY);
      } finally {
        setIsLoading(false);
      }
    };

    loadReportingCurrency();
  }, [isAuthenticated]);

  /**
   * Update the reporting currency preference
   * - Calls backend to persist the preference
   * - Updates local state
   * - Increments currencyVersion to trigger re-fetches across the app
   *
   * @param {string} newCurrency - The new reporting currency code (e.g., 'USD', 'SGD')
   * @returns {Promise<boolean>} - True if update succeeded, false otherwise
   */
  const updateReportingCurrency = useCallback(async (newCurrency) => {
    if (!newCurrency || newCurrency === reportingCurrency) {
      return true; // No change needed
    }

    try {
      const result = await fxService.setReportingCurrency(newCurrency);

      if (result.success) {
        setReportingCurrency(newCurrency);
        // Increment version to trigger re-fetches in components watching this value
        setCurrencyVersion(prev => prev + 1);
        return true;
      }

      console.error('Failed to update reporting currency:', result);
      return false;
    } catch (err) {
      console.error('Error updating reporting currency:', err);
      return false;
    }
  }, [reportingCurrency]);

  /**
   * Force refresh the currency preference from the backend
   * Useful if settings might have been changed in another tab/window
   */
  const refreshCurrency = useCallback(async ({ force = false } = {}) => {
    // Platform audit 2026-06-28 (C1): `force` bumps currencyVersion even when
    // the currency VALUE is unchanged. The demo-mode toggle relies on this —
    // when the demo user's reporting currency matches the real user's, the
    // old value-diff guard skipped the bump, so open pages kept showing the
    // previous dataset (real numbers under the demo banner, or demo numbers
    // presented as "My Portfolio" after exit).
    try {
      const result = await fxService.getReportingCurrency();

      if (result.success && result.data?.reportingCurrency) {
        const newCurrency = result.data.reportingCurrency;
        if (newCurrency !== reportingCurrency) {
          setReportingCurrency(newCurrency);
          setCurrencyVersion(prev => prev + 1);
          return;
        }
      }
      if (force) setCurrencyVersion(prev => prev + 1);
    } catch (err) {
      console.error('Failed to refresh reporting currency:', err);
      // A forced refresh must still trigger the refetch — the dataset behind
      // the pages changed (demo toggle) even if the currency read failed.
      if (force) setCurrencyVersion(prev => prev + 1);
    }
  }, [reportingCurrency]);

  // Context value
  const value = {
    // State
    reportingCurrency,
    accountCurrency: ACCOUNT_CURRENCY,
    isLoading,
    loadError,
    currencyVersion,

    // Actions
    updateReportingCurrency,
    refreshCurrency
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

/**
 * Hook to access the currency context
 * Must be used within a CurrencyProvider
 *
 * @returns {{
 *   reportingCurrency: string,
 *   accountCurrency: string,
 *   isLoading: boolean,
 *   loadError: string|null,
 *   currencyVersion: number,
 *   updateReportingCurrency: (currency: string) => Promise<boolean>,
 *   refreshCurrency: () => Promise<void>
 * }}
 */
export function useCurrency() {
  const context = useContext(CurrencyContext);

  if (context === null) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }

  return context;
}

export default CurrencyContext;
